import React from 'react';
import { Seo } from '../components/Seo';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Trash2, Plus, Minus, Sparkles, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useContent } from '../context/ContentContext';
import { bundlesData } from '../data/content';

const priceNum = (p: { price: string }) => parseInt(p.price.replace(/\D/g, ''));

export const Cart: React.FC = () => {
  const { cart, cartGroups, removeFromCart, removeBundle, updateQuantity, clearCart, cartTotal, addToCart, addBundle, goToCheckout, checkoutUrl, isLoading } = useCart();
  const { products } = useContent();
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);

  // Determine suggestion based on cart contents
  const suggestion = (() => {
    const ids = new Set(cart.map((c) => c.id));
    const has = (id: string) => ids.has(id);

    const SHAMPOO = 'salt-free-shampoo';
    const OIL = 'hair-growth-oil';
    const MASK = 'hair-repair-mask';
    const NIGHT = 'night-renewal-cream';
    const ANTI = 'anti-aging-cream';

    const hairIds = ['salt-free-shampoo', 'scalp-balance-shampoo', 'hair-growth-oil', 'hair-repair-mask'];
    const faceIds = ['night-renewal-cream', 'anti-aging-cream'];

    const hairCount = hairIds.filter((id) => has(id)).length;
    const faceCount = faceIds.filter((id) => has(id)).length;

    // Bundle upgrades (2+ products of same category)
    if (faceCount >= 2 && !has('skin-glow-set')) {
      const bundle = bundlesData.find((b) => b.id === 'skin-glow-set');
      if (bundle) return { type: 'bundle' as const, bundle };
    }
    if (hairCount >= 2 && !has('hair-repair-set')) {
      const bundle = bundlesData.find((b) => b.id === 'hair-repair-set');
      if (bundle) return { type: 'bundle' as const, bundle };
    }

    // Single-product upsells
    if (has(SHAMPOO) && has(OIL) && !has(MASK)) {
      const p = products.find((x) => x.id === MASK);
      if (p) return { type: 'product' as const, product: p };
    }
    if (has(SHAMPOO) && !has(OIL)) {
      const p = products.find((x) => x.id === OIL);
      if (p) return { type: 'product' as const, product: p };
    }
    if (has(NIGHT) && !has(ANTI)) {
      const p = products.find((x) => x.id === ANTI);
      if (p) return { type: 'product' as const, product: p };
    }
    if (has(ANTI) && !has(NIGHT)) {
      const p = products.find((x) => x.id === NIGHT);
      if (p) return { type: 'product' as const, product: p };
    }
    return null;
  })();

  const handleAddSuggested = () => {
    if (!suggestion) return;
    if (suggestion.type === 'product') {
      const p = suggestion.product;
      addToCart({ id: p.id, name: p.name, price: priceNum(p), quantity: 1, image: p.image });
    } else {
      const b = suggestion.bundle;
      const items = b.productIds
        .map((id) => products.find((p) => p.id === id))
        .filter((p): p is NonNullable<typeof p> => Boolean(p));
      const original = items.reduce((s, p) => s + priceNum(p), 0);
      const total =
        (b as any).priceOverride != null
          ? Math.round((b as any).priceOverride)
          : Math.round(original * (1 - b.discount));
      addBundle({
        slug: b.id,
        title: b.name,
        image: b.image,
        price: total,
        discountCode: (b as any).discountCode,
        components: items.map((p) => ({
          id: p.id,
          name: p.name,
          price: priceNum(p),
          image: p.image,
          quantity: 1,
        })),
      });
    }
  };


  return (
    <>
      <Seo
        title="עגלת קניות | RIHAM Shop"
        description="העגלה שלך בחנות RIHAM — מוצרי טיפוח טבעיים ורוקחות צמחים."
        path="/cart"
      />
    <div className="bg-[#EAE6D7] min-h-[100dvh] pt-32 pb-24 text-[#412018]">
      <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply"></div>
      
      <div className="container mx-auto px-6 relative z-10 max-w-6xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-[#67392b] text-sm tracking-[0.3em] uppercase block mb-4">ההזמנה שלך</span>
          <h1 className="text-5xl md:text-7xl font-serif mb-8 text-[#412018]">
            סל הקניות
          </h1>
        </motion.div>

        {cart.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white p-12 rounded-sm border border-[#412018]/10 shadow-2xl shadow-[#67392b]/10 text-center flex flex-col items-center justify-center min-h-[40vh]"
          >
            <div className="bg-[#F9F7F2] p-6 rounded-full mb-6 text-[#412018]/20">
              <ShoppingBag size={48} />
            </div>
            <h2 className="text-2xl font-serif text-[#412018] mb-4">סל הקניות שלך ריק</h2>
            <p className="text-[#412018]/60 font-light mb-8">
              נראה שעדיין לא הוספת מוצרים לסל. את מוזמנת לבקר בבוטיק שלנו ולהתרשם מהרוקחות הטבעית.
            </p>
            <Link 
              to="/shop" 
              className="bg-[#412018] text-[#fbbf25] px-8 py-4 uppercase tracking-widest font-bold text-sm hover:bg-[#67392b] transition-colors flex items-center gap-3"
            >
              <span>למעבר לבוטיק</span>
              <ArrowLeft size={16} />
            </Link>
          </motion.div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Cart Items */}
            <div className="w-full lg:w-2/3">
              <div className="border-b border-[#412018]/10 pb-4 mb-6 hidden md:grid grid-cols-12 gap-4 text-xs uppercase tracking-widest text-[#412018]/50">
                <div className="col-span-6">מוצר</div>
                <div className="col-span-2 text-center">מחיר</div>
                <div className="col-span-2 text-center">כמות</div>
                <div className="col-span-2 text-left">סה״כ</div>
              </div>

              <div className="space-y-6">
                {cartGroups.map((group) => {
                  if (group.type === 'bundle') {
                    return (
                      <div key={`bundle-${group.bundleId}`} className="border-b border-[#412018]/10 pb-6">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                          <div className="col-span-1 md:col-span-6 flex items-center gap-4">
                            <button
                              onClick={() => removeBundle(group.bundleId)}
                              className="text-[#412018]/40 hover:text-red-500 transition-colors"
                              aria-label={`הסר את ${group.bundleTitle}`}
                            >
                              <Trash2 size={18} />
                            </button>
                            <div className="w-20 h-20 bg-white rounded-sm overflow-hidden border border-[#412018]/5 flex-shrink-0 relative">
                              {group.bundleImage ? (
                                <img src={group.bundleImage} alt={group.bundleTitle} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full grid grid-cols-2 gap-px bg-[#412018]/20">
                                  {group.items.slice(0, 4).map((it) => (
                                    <img key={it.id} src={it.image} alt="" className="w-full h-full object-cover" />
                                  ))}
                                </div>
                              )}
                              <div className="absolute top-0 right-0 bg-[#fbbf25] text-[#412018] p-0.5">
                                <Package size={12} />
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] uppercase tracking-[0.25em] text-[#67392b] font-bold mb-1">מארז</div>
                              <div className="font-serif text-lg text-[#412018] leading-tight">{group.bundleTitle}</div>
                              <div className="text-xs text-[#412018]/60 mt-1">{group.items.length} פריטים כלולים</div>
                            </div>
                          </div>
                          <div className="col-span-1 md:col-span-2 text-center md:text-base text-[#412018]/70">
                            <span className="md:hidden text-xs uppercase text-[#412018]/40 mr-2">מחיר:</span>
                            ₪{group.bundlePrice}
                          </div>
                          <div className="col-span-1 md:col-span-2 flex justify-center text-xs text-[#412018]/50">
                            מארז — קבוע
                          </div>
                          <div className="col-span-1 md:col-span-2 text-left font-bold text-[#67392b]">
                            <span className="md:hidden text-xs uppercase text-[#412018]/40 mr-2">סה״כ:</span>
                            ₪{group.bundlePrice}
                          </div>
                        </div>
                        <ul className="mt-3 pr-24 space-y-1">
                          {group.items.map((it) => (
                            <li key={it.lineId ?? it.id} className="text-xs text-[#412018]/70 flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-[#412018]/40 flex-shrink-0" />
                              <span>{it.name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                  const item = group.item;
                  return (
                    <div key={item.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center border-b border-[#412018]/10 pb-6">
                      <div className="col-span-1 md:col-span-6 flex items-center gap-4">
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-[#412018]/40 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                        <div className="w-20 h-20 bg-white rounded-sm overflow-hidden border border-[#412018]/5 flex-shrink-0">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                        <Link to={`/shop/${item.id}`} className="font-serif text-lg hover:text-[#67392b] transition-colors">
                          {item.name}
                        </Link>
                      </div>

                      <div className="col-span-1 md:col-span-2 text-center md:text-base text-[#412018]/70">
                        <span className="md:hidden text-xs uppercase text-[#412018]/40 mr-2">מחיר:</span>
                        ₪{item.price}
                      </div>

                      <div className="col-span-1 md:col-span-2 flex justify-center">
                        <div className="flex items-center border border-[#412018]/20 rounded-full px-3 py-1 bg-white">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-1 hover:text-[#67392b] transition-colors"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-1 hover:text-[#67392b] transition-colors"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="col-span-1 md:col-span-2 text-left font-bold text-[#67392b]">
                        <span className="md:hidden text-xs uppercase text-[#412018]/40 mr-2">סה״כ:</span>
                        ₪{item.price * item.quantity}
                      </div>
                    </div>
                  );
                })}
              </div>


              {/* Smart upsell suggestion */}
              {suggestion && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-8 bg-white border border-[#412018]/10 p-5 md:p-6 flex flex-col md:flex-row items-stretch md:items-center gap-5"
                  dir="rtl"
                >
                  {suggestion.type === 'product' ? (
                    <>
                      <div className="w-20 h-20 bg-[#F9F7F2] overflow-hidden flex-shrink-0">
                        <img src={suggestion.product.image} alt={suggestion.product.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#67392b] font-bold mb-1.5">
                          <Sparkles size={12} />
                          <span>מומלץ עבורך</span>
                        </div>
                        <p className="font-serif text-lg text-[#412018] leading-tight">{suggestion.product.name}</p>
                        <p className="text-sm text-[#412018]/60 mt-1">{suggestion.product.tagline}</p>
                      </div>
                      <div className="flex items-center justify-between md:justify-end gap-4">
                        <span className="font-sans text-xl text-[#412018] tabular-nums">{suggestion.product.price}</span>
                        <button
                          onClick={handleAddSuggested}
                          className="bg-[#412018] text-[#fbbf25] px-5 py-3 text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#67392b] transition-colors"
                        >
                          הוסיפי לסל
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-20 h-20 bg-[#F9F7F2] overflow-hidden flex-shrink-0">
                        <img src={suggestion.bundle.image} alt={suggestion.bundle.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#67392b] font-bold mb-1.5">
                          <Sparkles size={12} />
                          <span>שדרגי למארז — חסכו {Math.round(suggestion.bundle.discount * 100)}%</span>
                        </div>
                        <p className="font-serif text-lg text-[#412018] leading-tight">{suggestion.bundle.name}</p>
                        <p className="text-sm text-[#412018]/60 mt-1">{suggestion.bundle.tagline}</p>
                      </div>
                      <div className="flex items-center justify-between md:justify-end gap-4">
                        <button
                          onClick={handleAddSuggested}
                          className="bg-[#412018] text-[#fbbf25] px-5 py-3 text-[11px] font-bold uppercase tracking-[0.25em] hover:bg-[#67392b] transition-colors"
                        >
                          הוסיפי מארז
                        </button>
                      </div>
                    </>
                  )}
                </motion.div>
              )}

              <div className="mt-8 flex justify-between items-center">
                <Link 
                  to="/shop" 
                  className="text-sm uppercase tracking-widest text-[#412018]/60 hover:text-[#67392b] transition-colors flex items-center gap-2"
                >
                  <ArrowLeft size={14} />
                  המשך קניות
                </Link>
                <button 
                  onClick={clearCart}
                  className="text-sm text-[#412018]/40 hover:text-red-500 transition-colors underline"
                >
                  רוקן סל
                </button>
              </div>
            </div>

            {/* Order Summary */}
            <div className="w-full lg:w-1/3">
              <div className="bg-white p-8 border border-[#412018]/10 shadow-sm sticky top-32">
                <h2 className="text-xl font-serif mb-6 border-b border-[#412018]/10 pb-4">סיכום הזמנה</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between text-[#412018]/70">
                    <span>סכום ביניים</span>
                    <span>₪{cartTotal}</span>
                  </div>
                  <div className="flex justify-between text-[#412018]/70">
                    <span>משלוח</span>
                    <span>חישוב בקופה</span>
                  </div>
                  <div className="border-t border-[#412018]/10 pt-4 flex justify-between font-bold text-xl text-[#67392b]">
                    <span>סה״כ</span>
                    <span>₪{cartTotal}</span>
                  </div>
                </div>

                <label className="flex items-start gap-3 mb-4 cursor-pointer text-sm text-[#412018]/80 select-none">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-[#412018] flex-shrink-0 cursor-pointer"
                  />
                  <span>
                    אני מאשר/ת שקראתי ואני מסכים/ה ל
                    <Link
                      to="/policies/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#67392b] underline hover:text-[#412018] transition-colors mx-1"
                    >
                      תקנון האתר
                    </Link>
                  </span>
                </label>
                <button
                  onClick={goToCheckout}
                  disabled={!checkoutUrl || isLoading || !agreedToTerms}
                  className="w-full bg-[#412018] text-[#fbbf25] py-4 uppercase tracking-[0.2em] hover:bg-[#67392b] hover:text-white transition-colors duration-500 flex items-center justify-center gap-4 group font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{isLoading ? 'מעבד...' : 'מעבר לתשלום'}</span>
                  <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};
